import type { SiteLang } from "@/hooks/useLanguage";
import {
  Compass,
  Stethoscope,
  Pill,
  HeartPulse,
  ShieldCheck,
  FlaskConical,
  Dna,
  ListChecks,
  TrendingUp,
  ScanSearch,
  FileCheck2,
  Thermometer,
  type LucideIcon,
} from "lucide-react";

/** Per-tool icon identity, keyed by href so all language variants share it.
 * Colour is always the default Oxford Blue accent: division colours are
 * reserved for content that belongs to that division. */
export const toolVisual: Record<string, { icon: LucideIcon }> = {
  "/en/eu-readiness": { icon: Compass },
  "/en/eu-readiness-medtech": { icon: Stethoscope },
  "/es/eu-readiness-medtech": { icon: Stethoscope },
  "/en/eu-readiness-pharma": { icon: Pill },
  "/es/eu-readiness-pharma": { icon: Pill },
  "/en/eu-readiness-cardiology": { icon: HeartPulse },
  "/es/eu-readiness-cardiology": { icon: HeartPulse },
  "/en/annex-1-diagnostic": { icon: ShieldCheck },
  "https://ivdr-performance-studies.qbdgroup.com/": { icon: FlaskConical },
  "https://cdx-readiness.qbdgroup.com/": { icon: Dna },
  "/en/cdx-governance-readiness": { icon: ListChecks },
  "https://pv-scalability-assessment.lovable.app/": { icon: TrendingUp },
  "https://mdr-blind-spot-check.qbdgroup.com/": { icon: ScanSearch },
  "/es/checklist-registro-medical-devices-recops-aemps": { icon: FileCheck2 },
  "/es/checklist-cualificacion-sistemas-termicos-gmp": { icon: Thermometer },
};

export interface OnlineTool {
  href: string;
  title: string;
  desc: string;
  external?: boolean;
}

const EN: OnlineTool[] = [
  {
    href: "/en/eu-readiness",
    title: "EU Readiness Assessments",
    desc: "Start here: pick the readiness assessment that matches your product, trial or organization.",
  },
  {
    href: "/en/eu-readiness-medtech",
    title: "EU MDR Readiness Assessment",
    desc: "Benchmark your medical device against EU MDR requirements and get a personalized report.",
  },
  {
    href: "/en/eu-readiness-pharma",
    title: "EU Pharma Readiness Assessment",
    desc: "Evaluate your drug or biologic against EU market-entry requirements across CTD, GMP, PV and more.",
  },
  {
    href: "/en/eu-readiness-cardiology",
    title: "EU Cardiology Trial Readiness Checklist",
    desc: "Check your cardiology clinical trial's readiness for EU CTR submission.",
  },
  {
    href: "/en/annex-1-diagnostic",
    title: "Annex 1 Compliance Diagnostic",
    desc: "Score your sterile manufacturing contamination control strategy against EU GMP Annex 1 in 20 questions.",
  },
  {
    href: "https://ivdr-performance-studies.qbdgroup.com/",
    title: "IVDR Performance Studies Assessment",
    desc: "Find out whether your IVD performance study design meets IVDR evidence expectations.",
    external: true,
  },
  {
    href: "https://cdx-readiness.qbdgroup.com/",
    title: "Companion Diagnostics (CDx) Readiness Check",
    desc: "Assess how ready your CDx programme is for co-development, regulatory review and market access.",
    external: true,
  },
  {
    href: "/en/cdx-governance-readiness",
    title: "CDx Combined Study Governance Readiness Checklist",
    desc: "Check whether roles, sponsorship and oversight in your CDx performance study are clearly governed under IVDR.",
  },
  {
    href: "https://pv-scalability-assessment.lovable.app/",
    title: "Pharmacovigilance Scalability Assessment",
    desc: "Test whether your PV system can absorb growth in case volume, products and markets.",
    external: true,
  },
  {
    href: "https://mdr-blind-spot-check.qbdgroup.com/",
    title: "MDR Blind Spot Check",
    desc: "Uncover the MDR compliance gaps most manufacturers overlook in their technical documentation.",
    external: true,
  },
];

const ES: OnlineTool[] = [
  {
    href: "/es/checklist-registro-medical-devices-recops-aemps",
    title: "Checklist de Registro de Medical Devices en España (RECOPS)",
    desc: "Autoevalúa en 5 minutos si estás listo para registrar tus Medical Devices e IVD en RECOPS, la aplicación de la AEMPS.",
  },
  {
    href: "/es/checklist-cualificacion-sistemas-termicos-gmp",
    title: "Checklist de Cualificación de Sistemas Térmicos GMP",
    desc: "Comprueba en 5 minutos si la cualificación de tus autoclaves, cámaras de estabilidad y sistemas WFI superaría una auditoría GMP.",
  },
  {
    href: "/es/eu-readiness-medtech",
    title: "Evaluación de Preparación para el MDR de la UE",
    desc: "Compara tu dispositivo médico con los requisitos del MDR de la UE y obtén un informe personalizado.",
  },
  {
    href: "/es/eu-readiness-pharma",
    title: "Evaluación de Preparación Farmacéutica para la UE",
    desc: "Evalúa tu medicamento o producto biológico frente a los requisitos de acceso al mercado europeo en CTD, GMP, FV y más.",
  },
  {
    href: "/es/eu-readiness-cardiology",
    title: "Checklist de Preparación de Ensayos de Cardiología para la UE",
    desc: "Comprueba la preparación de tu ensayo clínico de cardiología para la presentación conforme al CTR de la UE.",
  },
  {
    href: "/en/annex-1-diagnostic",
    title: "Diagnóstico de Cumplimiento del Anexo 1",
    desc: "Evalúa tu estrategia de control de la contaminación frente al Anexo 1 de las GMP de la UE (en inglés).",
  },
  {
    href: "https://ivdr-performance-studies.qbdgroup.com/",
    title: "Evaluación de Estudios de Rendimiento IVDR",
    desc: "Descubre si el diseño de tu estudio de rendimiento de IVD cumple las expectativas de evidencia del IVDR (en inglés).",
    external: true,
  },
  {
    href: "https://cdx-readiness.qbdgroup.com/",
    title: "Evaluación de Preparación de Companion Diagnostics (CDx)",
    desc: "Evalúa la preparación de tu programa de CDx para el codesarrollo, la revisión regulatoria y el acceso al mercado (en inglés).",
    external: true,
  },
  {
    href: "/en/cdx-governance-readiness",
    title: "Checklist de Preparación de Gobernanza CDx",
    desc: "Comprueba si los roles, el patrocinio y la supervisión de tu estudio de rendimiento CDx están bien definidos según el IVDR (en inglés).",
  },
  {
    href: "https://pv-scalability-assessment.lovable.app/",
    title: "Evaluación de Escalabilidad de Farmacovigilancia",
    desc: "Comprueba si tu sistema de FV puede absorber el crecimiento en volumen de casos, productos y mercados (en inglés).",
    external: true,
  },
  {
    href: "https://mdr-blind-spot-check.qbdgroup.com/",
    title: "MDR Blind Spot Check",
    desc: "Detecta las lagunas de cumplimiento del MDR que la mayoría de los fabricantes pasa por alto (en inglés).",
    external: true,
  },
];

const ZH: OnlineTool[] = [
  {
    href: "/en/eu-readiness",
    title: "欧盟准备度评估",
    desc: "从这里开始：选择与您的产品、临床试验或组织相匹配的准备度评估（英文）。",
  },
  {
    href: "/en/annex-1-diagnostic",
    title: "附录 1 合规诊断",
    desc: "通过 20 个问题评估无菌生产污染控制策略与欧盟 GMP 附录 1 的差距（英文）。",
  },
  {
    href: "https://ivdr-performance-studies.qbdgroup.com/",
    title: "IVDR 性能研究评估",
    desc: "了解您的 IVD 性能研究设计是否满足 IVDR 的证据要求（英文）。",
    external: true,
  },
  {
    href: "https://cdx-readiness.qbdgroup.com/",
    title: "伴随诊断（CDx）准备度检查",
    desc: "评估您的 CDx 项目在共同开发、法规审评和市场准入方面的准备情况（英文）。",
    external: true,
  },
  {
    href: "/en/cdx-governance-readiness",
    title: "CDx 治理准备度检查清单",
    desc: "检查您的 CDx 性能研究中的角色、申办责任和监督是否符合 IVDR 要求（英文）。",
  },
  {
    href: "https://pv-scalability-assessment.lovable.app/",
    title: "药物警戒可扩展性评估",
    desc: "检验您的 PV 体系能否承受病例量、产品和市场的增长（英文）。",
    external: true,
  },
  {
    href: "https://mdr-blind-spot-check.qbdgroup.com/",
    title: "MDR 盲点检查",
    desc: "发现大多数制造商在技术文档中忽视的 MDR 合规缺口（英文）。",
    external: true,
  },
];

export const getOnlineTools = (lang: SiteLang): OnlineTool[] =>
  lang === "es" ? ES : lang === "zh-cn" ? ZH : EN;

/** How many tools are visible before the "See all" button. */
export const RECENT_TOOL_COUNT = 6;

/** The most recently added tools first: the arrays are kept in chronological
 * order (oldest first), so the tail is the newest and we show it newest-first. */
export const getRecentOnlineTools = (lang: SiteLang, count = RECENT_TOOL_COUNT): OnlineTool[] =>
  getOnlineTools(lang).slice(-count).reverse();

export const onlineToolsCopy = {
  en: {
    heading: "Online Tools & Assessments",
    intro: "Interactive diagnostics and checklists to benchmark your product, trial or quality system in minutes.",
    badge: "Assessment",
    cta: "Start",
    seeAll: "See all assessments",
    showLess: "Show fewer",
  },
  es: {
    heading: "Herramientas y Evaluaciones Online",
    intro: "Diagnósticos y checklists interactivos para evaluar tu producto, ensayo o sistema de calidad en minutos.",
    badge: "Evaluación",
    cta: "Comenzar",
    seeAll: "Ver todas las evaluaciones",
    showLess: "Mostrar menos",
  },
  "zh-cn": {
    heading: "在线工具与评估",
    intro: "交互式诊断与检查清单，几分钟内评估您的产品、临床试验或质量体系。",
    badge: "评估",
    cta: "开始",
    seeAll: "查看全部评估",
    showLess: "收起",
  },
} as const;
